import { useFindAlldepartmentsQuery } from '../../api/api-hooks';
import {
  DataGrid,
  GridColDef,
  GridRowParams,
  GridSelectionModel,
} from '@mui/x-data-grid';
import { CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { Item } from '../../utils/sharedTypes';
import { Department, Company } from '../../api/api-generated';
import { DepartmentFilter } from './departmentsTypes';
import { differenceInMonths } from 'date-fns';
import { useGridSort } from '../../hooks/useGridSort';
import { filterDepartment } from './departmentsUtils';

interface Props {
  setSelectedDepartments: React.Dispatch<React.SetStateAction<Item[]>>;
  extraFilters?: {
    company?: Pick<
      Company,
      'id' | 'companyName' | 'supportPurchases' | 'departments'
    >;
  };
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
  filters?: DepartmentFilter;
  supportPage?: boolean;
}

const DepartmentsTable = ({
  setSelectedDepartments,
  extraFilters,
  setEditModalOpenFlag,
  supportPage = false,
  filters,
}: Props) => {
  const { data: allDepartments } = useFindAlldepartmentsQuery({});
  const { sortModel, handleSortModelChange } = useGridSort(
    supportPage ? 'expirationDate' : 'id',
    'asc'
  );
  if (allDepartments === undefined)
    return <CircularProgress></CircularProgress>;
  const filteredDepartments = allDepartments.filter((department) => {
    if (extraFilters && department.companyId !== extraFilters.company?.id)
      return false;
    if (!filters) return true;
    return filterDepartment(department, filters);
  });
  // Define data for display in DataGrid
  const columns: GridColDef[] = [
    { field: 'id', headerName: '', hide: true },
    {
      field: 'companyName',
      headerName: 'Company',
      width: 150,
      hide: !supportPage,
    },
    {
      field: 'expirationDate',
      headerName: 'Expiration Date',
      width: 150,
      hide: !supportPage,
    },
    {
      field: 'timeLeft',
      headerName: 'Months Left',
      width: 150,
      hide: !supportPage,
      renderCell: (params) => {
        const difference = differenceInMonths(
          new Date(params.row.expirationDate),
          new Date()
        );
        return difference === 0 ? (
          <Typography variant='body2' color='#ad6f03'>
            Less Than 1
          </Typography>
        ) : difference > 0 ? (
          <Typography variant='body2'>{difference + ' Month(s)'}</Typography>
        ) : (
          <Typography variant='body2' color='red'>
            Expired
          </Typography>
        );
      },
    },
    { field: 'departmentName', headerName: 'Department Name', width: 150 },
    {
      field: 'lastContacted',
      headerName: 'Last Contacted',
      width: 150,
      hide: !supportPage,
    },
    { field: 'contactName', headerName: 'Contact', width: 180 },
    { field: 'contactPhone', headerName: 'Phone', width: 180 },
    { field: 'contactEmail', headerName: 'Email', width: 180 },
  ];
  const rows = filteredDepartments.map((item: Department) => ({
    ...item,
    companyName: item.company.companyName,
    lastContacted: item.lastContacted ? item.lastContacted : 'Never',
  }));
  const handleTableSelection = (model: GridSelectionModel) => {
    setSelectedDepartments(
      filteredDepartments.filter((supportPurchase) =>
        model.includes(supportPurchase.id)
      )
    );
  };
  const handleRowClick = (params: GridRowParams) => {
    setSelectedDepartments(
      allDepartments.filter(
        (department: Department) => department.id === params.row.id
      )
    );
    setEditModalOpenFlag(true);
  };
  return (
    <div style={{ paddingBottom: '1rem', width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSize={100}
        checkboxSelection
        disableSelectionOnClick
        autoHeight
        disableColumnMenu={true}
        onSelectionModelChange={handleTableSelection}
        onRowClick={handleRowClick}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
      />
    </div>
  );
};

export default DepartmentsTable;
